import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Chip,
    TextField,
    InputAdornment,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Search as SearchIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    Schedule as ScheduleIcon,
    Edit as EditIcon,
    Stop as StopIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { Workflow, WorkflowStep, NodeType } from '../../types';
import type { DocumentType } from '@aws-amplify/core/internals/utils';

interface ApiWorkflow {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
    steps: WorkflowStep[];
    schedule?: string;
    createdAt: string;
    updatedAt: string;
}

interface WorkflowListResponse {
    items: ApiWorkflow[];
}

const convertToWorkflow = (apiWorkflow: ApiWorkflow): Workflow => ({
    ...apiWorkflow,
    isActive: apiWorkflow.enabled,
    nodes: apiWorkflow.steps.map((step, index) => ({
        id: `node-${index}`,
        type: step.type,
        name: step.name,
        description: step.description,
        config: step.config,
        position: { x: 100 + index * 200, y: 100 },
        data: { label: step.name, type: step.type }
    })),
    edges: apiWorkflow.steps.slice(0, -1).map((_, index) => ({
        id: `edge-${index}`,
        source: `node-${index}`,
        target: `node-${index + 1}`,
        type: 'default'
    }))
});

const isWorkflowListResponse = (data: unknown): data is WorkflowListResponse => {
    return (
        typeof data === 'object' &&
        data !== null &&
        'items' in data &&
        Array.isArray((data as WorkflowListResponse).items)
    );
};

const isApiWorkflow = (data: unknown): data is ApiWorkflow => {
    return (
        typeof data === 'object' &&
        data !== null &&
        'id' in data &&
        typeof (data as ApiWorkflow).id === 'string' &&
        'name' in data &&
        typeof (data as ApiWorkflow).name === 'string' &&
        'description' in data &&
        typeof (data as ApiWorkflow).description === 'string' &&
        'enabled' in data &&
        typeof (data as ApiWorkflow).enabled === 'boolean' &&
        'steps' in data &&
        Array.isArray((data as ApiWorkflow).steps)
    );
};

export default function WorkflowList() {
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchWorkflows();
    }, []);

    const fetchWorkflows = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get authentication token
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }

            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/workflows`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Handle both array response and object with items property
            let workflowsData: unknown[] = [];

            if (Array.isArray(data)) {
                workflowsData = data;
            } else if (typeof data === 'object' && data !== null && 'items' in data && Array.isArray(data.items)) {
                workflowsData = data.items;
            } else {
                throw new Error('Invalid API response format');
            }

            const validWorkflows = workflowsData.filter(isApiWorkflow);
            setWorkflows(validWorkflows.map(convertToWorkflow));
        } catch (error) {
            console.error('Error fetching workflows:', error);
            setError('Failed to load workflows. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, workflowId: string) => {
        event.stopPropagation(); // Prevent card click
        setAnchorEl(event.currentTarget);
        setSelectedWorkflow(workflowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedWorkflow(null);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleWorkflowClick = (workflowId: string) => {
        navigate(`/workflows/${workflowId}`);
    };

    const handleCreateWorkflow = () => {
        navigate('/workflows/new');
    };

    const handleEditWorkflow = (workflowId: string) => {
        handleMenuClose();
        navigate(`/workflows/${workflowId}`);
    };

    const handleDeleteWorkflow = async (workflowId: string) => {
        try {
            handleMenuClose();
            setLoading(true);
            setError(null);

            // Get authentication token
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }

            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/workflows/${workflowId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Remove workflow from state
            setWorkflows(workflows.filter(w => w.id !== workflowId));
        } catch (error) {
            console.error('Error deleting workflow:', error);
            setError('Failed to delete workflow. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleWorkflow = async (workflowId: string, currentState: boolean) => {
        try {
            handleMenuClose();
            setLoading(true);
            setError(null);

            // Get authentication token
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }

            const workflow = workflows.find(w => w.id === workflowId);
            if (!workflow) return;

            const updatedWorkflow = {
                ...workflow,
                enabled: !currentState,
                isActive: !currentState
            };

            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/workflows/${workflowId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    name: workflow.name,
                    description: workflow.description,
                    enabled: !currentState,
                    steps: workflow.steps,
                    schedule: workflow.schedule
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!isApiWorkflow(data)) {
                throw new Error('Invalid response data');
            }

            // Update workflow in state
            setWorkflows(workflows.map(w =>
                w.id === workflowId ? convertToWorkflow(data) : w
            ));
        } catch (error) {
            console.error('Error updating workflow:', error);
            setError('Failed to update workflow. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteWorkflow = async (workflowId: string) => {
        try {
            handleMenuClose();
            setLoading(true);
            setError(null);

            // Get authentication token
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }

            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/executions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ workflowId })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            navigate('/executions');
        } catch (error) {
            console.error('Error executing workflow:', error);
            setError('Failed to execute workflow. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Filter workflows based on search term
    const filteredWorkflows = workflows.filter(
        workflow =>
            workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            workflow.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1">
                    Workflows
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateWorkflow}
                >
                    Create Workflow
                </Button>
            </Box>

            <Box sx={{ mb: 4 }}>
                <TextField
                    fullWidth
                    placeholder="Search workflows..."
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && workflows.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : filteredWorkflows.length === 0 ? (
                <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                    {searchTerm ? 'No workflows match your search' : 'No workflows created yet'}
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {filteredWorkflows.map((workflow) => (
                        <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 calc(50% - 12px)', md: '0 0 calc(33.33% - 16px)' } }} key={workflow.id}>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': { boxShadow: 3 }
                                }}
                                onClick={() => handleWorkflowClick(workflow.id)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="h6" noWrap>
                                            {workflow.name}
                                        </Typography>
                                        <IconButton
                                            onClick={(e) => handleMenuOpen(e, workflow.id)}
                                            size="small"
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </Box>
                                    <Typography color="textSecondary" sx={{ mb: 2, maxHeight: '3em', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {workflow.description || 'No description'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            icon={workflow.isActive ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                                            label={workflow.isActive ? 'Active' : 'Inactive'}
                                            color={workflow.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                        {workflow.schedule && (
                                            <Chip
                                                icon={<ScheduleIcon fontSize="small" />}
                                                label="Scheduled"
                                                color="info"
                                                size="small"
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    ))}
                </Box>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => selectedWorkflow && handleEditWorkflow(selectedWorkflow)}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                </MenuItem>
                <MenuItem onClick={() => selectedWorkflow && handleExecuteWorkflow(selectedWorkflow)}>
                    <PlayIcon fontSize="small" sx={{ mr: 1 }} /> Execute
                </MenuItem>
                {selectedWorkflow && (
                    <MenuItem onClick={() => selectedWorkflow && handleToggleWorkflow(
                        selectedWorkflow,
                        workflows.find(w => w.id === selectedWorkflow)?.isActive || false
                    )}>
                        {workflows.find(w => w.id === selectedWorkflow)?.isActive ? (
                            <>
                                <StopIcon fontSize="small" sx={{ mr: 1 }} /> Deactivate
                            </>
                        ) : (
                            <>
                                <PlayIcon fontSize="small" sx={{ mr: 1 }} /> Activate
                            </>
                        )}
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => selectedWorkflow && handleDeleteWorkflow(selectedWorkflow)}
                    sx={{ color: 'error.main' }}
                >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                </MenuItem>
            </Menu>
        </Box>
    );
}
